import React, { useState, useEffect } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import { useSearchParams } from 'react-router-dom';
import { 
  Server, 
  Crown, 
  Settings, 
  Users, 
  Gamepad2,
  CreditCard,
  AlertTriangle,
  CheckCircle
} from 'lucide-react';
import { discordAPI, stripeAPI, serverSubscriptionsAPI } from '../services/api';
import ServerSubscription from '../components/ServerSubscription';
import BotInvite from '../components/BotInvite';
import LoadingSpinner from '../components/LoadingSpinner';
import toast from 'react-hot-toast';

const ServerManagement = () => {
  const [selectedGuild, setSelectedGuild] = useState(null);
  const [searchParams] = useSearchParams();
  const queryClient = useQueryClient();

  // Fetch user's Discord servers
  const { data: serversData, isLoading: serversLoading, error } = useQuery(
    'discordServers',
    () => discordAPI.getUserServers().then(res => res.data),
    {
      onError: (err) => {
        console.error('Discord servers error:', err);
        if (err.response?.status === 401) {
          // Don't show toast for auth errors, let AuthContext handle it
          console.log('Authentication error, redirecting to login');
        } else {
          toast.error('Failed to load Discord servers');
        }
      },
      retry: (failureCount, error) => {
        // Don't retry on authentication errors
        if (error?.response?.status === 401) {
          return false;
        }
        return failureCount < 2;
      }
    }
  );

  // Handle success/cancel parameters from Stripe
  useEffect(() => {
    const success = searchParams.get('success');
    const canceled = searchParams.get('canceled');
    const sessionId = searchParams.get('session_id');
    
    console.log('🔍 URL Parameters:', { success, canceled, sessionId });
    console.log('🔍 Current URL:', window.location.href);
    console.log('🔍 Servers data available:', !!serversData);
    console.log('🔍 Servers count:', serversData?.guilds?.length || 0);
    
    if (success === 'true' && sessionId) {
      console.log('✅ Payment success detected, starting verification...');
      
      // Verify the session and update subscription
      const verifySession = async () => {
        try {
          console.log('🔍 Verifying session:', sessionId);
          console.log('🔍 API endpoint:', `/api/server-subscriptions/verify-session/${sessionId}`);
          
          let response;
          try {
            // Try the main verification first
            response = await serverSubscriptionsAPI.verifySession(sessionId);
            console.log('🔍 Main verification response:', response);
          } catch (error) {
            console.log('⚠️ Main verification failed, trying simple verification...');
            console.log('⚠️ Error:', error.response?.data);
            
            // If main verification fails, try simple verification
            // Use the first available server as fallback
            const servers = serversData?.guilds || [];
            const fallbackServerId = servers.length > 0 ? servers[0].id : null;
            
            console.log('🔍 Available servers for fallback:', servers.length);
            console.log('🔍 Fallback server ID:', fallbackServerId);
            
            if (!fallbackServerId) {
              // If no servers available, try without specifying a server
              console.log('🔄 No servers available, using simple verification without server ID');
              response = await serverSubscriptionsAPI.verifySessionSimple(sessionId, null);
            } else {
              console.log('🔄 Using simple verification with server:', fallbackServerId);
              response = await serverSubscriptionsAPI.verifySessionSimple(sessionId, fallbackServerId);
            }
            console.log('🔍 Simple verification response:', response);
          }
          
          if (response.data.success) {
            console.log('✅ Session verification successful');
            toast.success('🎉 Subscription activated successfully! Your server now has premium features.');
            
            // Invalidate and refetch subscription data
            queryClient.invalidateQueries(['serverSubscription']);
            queryClient.invalidateQueries('discordServers');
            queryClient.invalidateQueries(['adminServerSubscriptions']);
            // Invalidate and refetch subscription data
            queryClient.invalidateQueries(['serverSubscription']);
            queryClient.invalidateQueries('discordServers');
            queryClient.invalidateQueries(['adminServerSubscriptions']);
            
            // Force a page refresh to ensure UI updates
            setTimeout(() => {
              window.location.reload();
            }, 2000);
          } else {
            console.error('❌ Session verification failed:', response.data);
            toast.error('Failed to verify subscription. Please contact support.');
          }
        } catch (error) {
          console.error('❌ Session verification error:', error);
          console.error('❌ Error details:', {
            message: error.message,
            response: error.response?.data,
            status: error.response?.status
          });
          toast.error('Failed to verify subscription. Please contact support.');
        }
      };
      
      // Wait a bit for servers data to load if it's not available yet
      if (!serversData && !serversLoading) {
        console.log('⏳ Waiting for servers data to load...');
        setTimeout(() => {
          verifySession();
        }, 1000);
      } else {
        verifySession();
      }
      
      // Clean up URL parameters
      window.history.replaceState({}, document.title, window.location.pathname);
    } else if (canceled === 'true') {
      console.log('❌ Payment canceled');
      toast.error('Subscription canceled. You can try again anytime.');
      // Clean up URL parameters
      window.history.replaceState({}, document.title, window.location.pathname);
    } else {
      console.log('ℹ️ No payment parameters detected');
    }
  }, [searchParams, queryClient, serversData, serversLoading]);

  // Extract servers from the response (API returns { guilds: [...] })
  const servers = Array.isArray(serversData?.guilds) ? serversData.guilds : [];
  
  // Debug logging
  console.log('Servers data:', serversData);
  console.log('Servers array:', servers);

  if (serversLoading) return <LoadingSpinner />;
  if (error) return <div className="text-red-500">Error loading servers</div>;

  return (
    <div className="space-y-6">
      <div className="flex items-center space-x-3">
        <Server className="h-8 w-8 text-discord-blurple" />
        <h1 className="text-2xl font-bold text-discord-lightest">Server Management</h1>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Server List */}
        <div className="lg:col-span-1">
          <div className="bg-discord-darker p-6 rounded-lg border border-discord-dark">
            <h2 className="text-lg font-semibold text-discord-lightest mb-4">Your Servers</h2>
            
            {!servers || servers.length === 0 ? (
              <div className="text-center py-8">
                <Server className="h-12 w-12 text-discord-light mx-auto mb-3" />
                <p className="text-discord-light">No servers found</p>
                <p className="text-discord-light text-sm mt-1">
                  Make sure the bot is added to your servers
                </p>
                <div className="mt-4">
                  <BotInvite variant="compact" />
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                {servers.map((server) => (
                  <ServerCard
                    key={server.id}
                    server={server}
                    isSelected={selectedGuild === server.id}
                    onClick={() => setSelectedGuild(server.id)}
                  />
                ))}
                {/* Add Server button - always visible when servers exist */}
                <div className="mt-4 pt-3 border-t border-discord-dark">
                  <BotInvite variant="compact" />
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Server Details & Subscription */}
        <div className="lg:col-span-2">
          {selectedGuild ? (
            <ServerDetails guildId={selectedGuild} serverName={servers.find(s => s.id === selectedGuild)?.name} />
          ) : (
            <div className="bg-discord-darker p-8 rounded-lg border border-discord-dark text-center">
              <Server className="h-16 w-16 text-discord-light mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-discord-lightest mb-2">
                Select a Server
              </h3>
              <p className="text-discord-light">
                Choose a server from the list to manage its subscription and settings.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const ServerCard = ({ server, isSelected, onClick }) => {
  // Fetch subscription data for this server
  const { data: subscriptionData } = useQuery(
    ['serverSubscription', server.id],
    () => serverSubscriptionsAPI.getServerSubscription(server.id, server.name).then(res => res.data),
    {
      enabled: !!server.id,
      retry: (failureCount, error) => {
        // Don't retry on 404 errors (no subscription exists)
        if (error?.response?.status === 404) {
          return false;
        }
        return failureCount < 2;
      }
    }
  );

  const subscription = subscriptionData?.data?.subscription;
  const planType = subscription?.planType || 'free';

  const handleClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    onClick();
  };

  // Get badge styling based on plan type
  const getBadgeStyle = (plan) => {
    switch (plan) {
      case 'pro':
        return 'bg-gradient-to-r from-purple-500 to-pink-500 text-white';
      case 'premium':
        return 'bg-gradient-to-r from-yellow-500 to-orange-500 text-white';
      case 'enterprise':
        return 'bg-gradient-to-r from-gray-700 to-gray-900 text-white';
      default:
        return 'bg-discord-dark text-discord-light';
    }
  };

  const getBadgeIcon = (plan) => {
    switch (plan) {
      case 'pro':
        return '⭐';
      case 'premium':
        return '💎';
      case 'enterprise':
        return '🏢';
      default:
        return '🆓';
    }
  };

  return (
    <div
      onClick={handleClick}
      className={`p-4 rounded-lg border cursor-pointer transition-colors ${
        isSelected
          ? 'border-discord-blurple bg-discord-blurple/10'
          : 'border-discord-dark hover:border-discord-light/50'
      }`}
    >
      <div className="flex items-center space-x-3">
        {server.icon ? (
          <img
            src={`https://cdn.discordapp.com/icons/${server.id}/${server.icon}.png?size=64`}
            alt={server.name}
            className="w-12 h-12 rounded-full"
          />
        ) : (
          <div className="w-12 h-12 rounded-full bg-discord-dark flex items-center justify-center">
            <Server className="h-6 w-6 text-discord-light" />
          </div>
        )}
        
        <div className="flex-1 min-w-0">
          <div className="flex items-center space-x-2 mb-1">
            <h3 className="text-discord-lightest font-medium truncate">
              {server.name}
            </h3>
            {/* Subscription Badge */}
            <span className={`px-2 py-1 rounded-full text-xs font-medium flex items-center space-x-1 ${getBadgeStyle(planType)}`}>
              <span>{getBadgeIcon(planType)}</span>
              <span>{planType.charAt(0).toUpperCase() + planType.slice(1)}</span>
            </span>
          </div>
          <p className="text-discord-light text-sm">
            {server.memberCount} members
          </p>
        </div>

        <div className="flex flex-col items-end space-y-1">
          {server.owner && (
            <Crown className="h-4 w-4 text-yellow-400" />
          )}
          {subscription?.status && subscription.status !== 'active' && (
            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
              subscription.status === 'trialing' ? 'bg-blue-900 text-blue-400' :
              subscription.status === 'past_due' ? 'bg-yellow-900 text-yellow-400' :
              'bg-red-900 text-red-400'
            }`}>
              {subscription.status.charAt(0).toUpperCase() + subscription.status.slice(1)}
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

const ServerDetails = ({ guildId, serverName }) => {
  const [activeTab, setActiveTab] = useState('subscription');

  // Fetch server details
  const { data: serverDetails, isLoading: serverLoading } = useQuery(
    ['serverDetails', guildId],
    () => discordAPI.getServerDetails(guildId).then(res => res.data),
    {
      enabled: !!guildId,
      onError: (err) => {
        console.error('Server details error:', err);
        if (err.response?.status === 401) {
          console.log('Authentication error in server details');
        } else {
          toast.error('Failed to load server details');
        }
      },
      retry: (failureCount, error) => {
        if (error?.response?.status === 401) {
          return false;
        }
        return failureCount < 2;
      }
    }
  );

  const tabs = [
    { id: 'subscription', name: 'Subscription', icon: CreditCard, description: 'Manage server subscription and billing' },
    { id: 'settings', name: 'Bot Settings', icon: Settings, description: 'Configure bot behavior and preferences' },
    { id: 'analytics', name: 'Analytics', icon: Gamepad2, description: 'View server activity and game statistics' }
  ];

  if (serverLoading) return <LoadingSpinner />;

  return (
    <div className="space-y-6">
      {/* Server Header */}
      <div className="bg-discord-darker p-6 rounded-lg border border-discord-dark">
        <div className="flex items-center space-x-4">
          {serverDetails?.icon ? (
            <img
              src={`https://cdn.discordapp.com/icons/${guildId}/${serverDetails.icon}.png?size=128`}
              alt={serverDetails.name}
              className="w-16 h-16 rounded-full"
            />
          ) : (
            <div className="w-16 h-16 rounded-full bg-discord-dark flex items-center justify-center">
              <Server className="h-8 w-8 text-discord-light" />
            </div>
          )}
          
          <div>
            <h2 className="text-xl font-bold text-discord-lightest">
              {serverDetails?.name || 'Unknown Server'}
            </h2>
            <p className="text-discord-light">
              {serverDetails?.memberCount || 0} members
            </p>
            {serverDetails?.owner && (
              <div className="flex items-center space-x-1 mt-1">
                <Crown className="h-4 w-4 text-yellow-400" />
                <span className="text-discord-light text-sm">You own this server</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-discord-darker rounded-lg border border-discord-dark">
        <div className="flex border-b border-discord-dark">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex flex-col items-start space-y-1 px-6 py-4 font-medium transition-colors ${
                activeTab === tab.id
                  ? 'text-discord-blurple border-b-2 border-discord-blurple'
                  : 'text-discord-light hover:text-discord-lightest'
              }`}
            >
              <div className="flex items-center space-x-2">
                <tab.icon className="h-4 w-4" />
                <span>{tab.name}</span>
              </div>
              <span className="text-xs text-discord-light opacity-75">
                {tab.description}
              </span>
            </button>
          ))}
        </div>

        <div className="p-6">
          {activeTab === 'subscription' && (
            <ServerSubscription 
              guildId={guildId} 
              guildName={serverName || serverDetails?.name} 
            />
          )}

          {activeTab === 'settings' && (
            <ServerSettings guildId={guildId} />
          )}

          {activeTab === 'analytics' && (
            <ServerAnalytics guildId={guildId} />
          )}
        </div>
      </div>
    </div>
  );
};

const ServerSettings = ({ guildId }) => {
  const { data: settings, isLoading } = useQuery(
    ['serverSettings', guildId],
    () => discordAPI.getServerSettings(guildId).then(res => res.data),
    {
      enabled: !!guildId
    }
  );

  if (isLoading) return <LoadingSpinner />;

  return (
    <div className="space-y-6">
      <h3 className="text-lg font-semibold text-discord-lightest">Bot Settings</h3>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-discord-light text-sm font-medium mb-2">
            Command Prefix
          </label>
          <input
            type="text"
            defaultValue={settings?.prefix || '!'}
            className="w-full input-field"
            placeholder="!"
          />
        </div>

        <div>
          <label className="block text-discord-light text-sm font-medium mb-2">
            Welcome Channel
          </label>
          <select className="w-full input-field">
            <option value="">Select a channel</option>
            {/* Channel options would be populated from Discord API */}
          </select>
        </div>
      </div>

      <div>
        <label className="block text-discord-light text-sm font-medium mb-2">
          Enabled Games
        </label>
        <div className="space-y-2">
          {['tic-tac-toe', 'number-guess', 'rock-paper-scissors', 'battle-grid'].map((game) => (
            <label key={game} className="flex items-center space-x-2">
              <input
                type="checkbox"
                defaultChecked={settings?.enabledGames?.includes(game) || false}
                className="rounded border-discord-dark bg-discord-darker text-discord-blurple"
              />
              <span className="text-discord-light capitalize">
                {game.replace('-', ' ')}
              </span>
            </label>
          ))}
        </div>
      </div>

      <button className="btn btn-primary">
        Save Settings
      </button>
    </div>
  );
};

const ServerAnalytics = ({ guildId }) => {
  const { data: analytics, isLoading, error } = useQuery(
    ['serverAnalytics', guildId],
    () => discordAPI.getServerAnalytics(guildId).then(res => res.data),
    {
      enabled: !!guildId,
      onError: (err) => {
        console.error('Server analytics error:', err);
        toast.error('Failed to load server analytics');
      },
      retry: 1
    }
  );

  if (isLoading) return <LoadingSpinner />;

  if (error) {
    return (
      <div className="space-y-6">
        <h3 className="text-lg font-semibold text-discord-lightest">Server Analytics</h3>
        <div className="bg-discord-darker p-6 rounded-lg border border-discord-dark text-center">
          <AlertTriangle className="h-12 w-12 text-discord-light mx-auto mb-3" />
          <h4 className="text-discord-lightest font-medium mb-2">Analytics Unavailable</h4>
          <p className="text-discord-light">
            Unable to load server analytics. This may be because no games have been played yet.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <h3 className="text-lg font-semibold text-discord-lightest">Server Analytics</h3>
      
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-discord-darkest p-4 rounded-lg border border-discord-dark">
          <div className="flex items-center space-x-3">
            <Gamepad2 className="h-8 w-8 text-discord-blurple" />
            <div>
              <p className="text-discord-light text-sm">Total Games</p>
              <p className="text-2xl font-bold text-discord-lightest">
                {analytics?.totalGames || 0}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-discord-darkest p-4 rounded-lg border border-discord-dark">
          <div className="flex items-center space-x-3">
            <Users className="h-8 w-8 text-discord-blurple" />
            <div>
              <p className="text-discord-light text-sm">Active Players</p>
              <p className="text-2xl font-bold text-discord-lightest">
                {analytics?.activePlayers || 0}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-discord-darkest p-4 rounded-lg border border-discord-dark">
          <div className="flex items-center space-x-3">
            <CheckCircle className="h-8 w-8 text-discord-blurple" />
            <div>
              <p className="text-discord-light text-sm">Completion Rate</p>
              <p className="text-2xl font-bold text-discord-lightest">
                {analytics?.completionRate || 0}%
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-discord-darkest p-4 rounded-lg border border-discord-dark">
        <h4 className="text-discord-lightest font-medium mb-3">Game Popularity</h4>
        <div className="space-y-2">
          {analytics?.gameStats?.map((game, index) => (
            <div key={index} className="flex items-center justify-between">
              <span className="text-discord-light capitalize">
                {game.name.replace('-', ' ')}
              </span>
              <span className="text-discord-lightest font-medium">
                {game.count} games
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Member List */}
      <ServerMemberList guildId={guildId} />
    </div>
  );
};

const ServerMemberList = ({ guildId }) => {
  const { data: membersData, isLoading, error } = useQuery(
    ['serverMembers', guildId],
    () => discordAPI.getServerMembers(guildId).then(res => res.data),
    {
      enabled: !!guildId,
      onError: (err) => {
        console.error('Server members error:', err);
        toast.error('Failed to load server members');
      },
      retry: 1
    }
  );

  const members = membersData?.data?.members || [];

  if (isLoading) return <LoadingSpinner />;

  if (error) {
    return (
      <div className="bg-discord-darkest p-4 rounded-lg border border-discord-dark text-center">
        <AlertTriangle className="h-8 w-8 text-discord-light mx-auto mb-2" />
        <h4 className="text-discord-lightest font-medium mb-1">Members Unavailable</h4>
        <p className="text-discord-light text-sm">
          Unable to load member list. This may be because no games have been played yet.
        </p>
      </div>
    );
  }

  return (
    <div className="bg-discord-darkest p-4 rounded-lg border border-discord-dark">
      <h4 className="text-discord-lightest font-medium mb-3">Active Members</h4>
      
      {members.length === 0 ? (
        <div className="text-center py-6">
          <Users className="h-8 w-8 text-discord-light mx-auto mb-2" />
          <p className="text-discord-light text-sm">No active members found</p>
          <p className="text-discord-light text-xs mt-1">
            Members will appear here once they start playing games
          </p>
        </div>
      ) : (
        <div className="space-y-2 max-h-64 overflow-y-auto">
          {members.slice(0, 10).map((member, index) => (
            <div key={member.playerId} className="flex items-center justify-between p-2 rounded bg-discord-darker">
              <div className="flex items-center space-x-3">
                <div className="flex items-center justify-center w-6 h-6 rounded-full bg-discord-blurple text-xs font-bold text-white">
                  {index + 1}
                </div>
                <div>
                  <span className="text-discord-lightest text-sm font-medium">
                    {member.username}
                  </span>
                  <div className="flex items-center space-x-2 text-xs text-discord-light">
                    <span>{member.totalGames} games</span>
                    <span>•</span>
                    <span>{member.winRate}% win rate</span>
                  </div>
                </div>
              </div>
              <div className="text-right">
                <div className="text-discord-lightest font-medium text-sm">
                  {member.totalPoints} pts
                </div>
                <div className="text-xs text-discord-light">
                  {member.gameTypes.length} game{member.gameTypes.length !== 1 ? 's' : ''}
                </div>
              </div>
            </div>
          ))}
          
          {members.length > 10 && (
            <div className="text-center py-2">
              <span className="text-discord-light text-xs">
                Showing top 10 of {members.length} members
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ServerManagement;
